import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

/**
 * Grant free launch entitlement for a specific game (Admin only)
 * @param {string} userId - Target user ID
 * @param {string} gameId - Game ID to grant launch access for
 * @param {string} gameTitle - Game title for logging
 * @param {string} reason - Reason for granting (optional)
 * @returns {Promise<Object>} Result of the grant operation
 */
export const grantFreeLaunch = async (userId, gameId, gameTitle, reason = '') => {
  try {
    const grantFreeLaunchFunction = httpsCallable(functions, 'grantFreeLaunch');
    const result = await grantFreeLaunchFunction({
      userId,
      gameId,
      gameTitle,
      reason
    });
    return result.data;
  } catch (error) {
    console.error('Error granting free launch:', error);
    throw new Error(error.message || 'Failed to grant free launch');
  }
};

/**
 * Grant free marketplace game access (Admin only)
 * @param {string} userId - Target user ID
 * @param {string} gameId - Game ID to grant access for
 * @param {string} gameTitle - Game title for logging
 * @param {string} reason - Reason for granting (optional)
 * @returns {Promise<Object>} Result of the grant operation
 */
export const grantFreeGameAccess = async (userId, gameId, gameTitle, reason = '') => {
  try {
    const grantFreeGameAccessFunction = httpsCallable(functions, 'grantFreeGameAccess');
    const result = await grantFreeGameAccessFunction({
      userId,
      gameId,
      gameTitle,
      reason
    });
    return result.data;
  } catch (error) {
    console.error('Error granting free game access:', error);
    throw new Error(error.message || 'Failed to grant free game access');
  }
};

/**
 * Grant temporary creator access (Admin only)
 * @param {string} userId - Target user ID
 * @param {number} durationHours - Duration in hours (default: 24)
 * @param {string} reason - Reason for granting (optional)
 * @returns {Promise<Object>} Result of the grant operation
 */
export const grantCreatorAccess = async (userId, durationHours = 24, reason = '') => {
  try {
    const grantCreatorAccessFunction = httpsCallable(functions, 'grantCreatorAccess');
    const result = await grantCreatorAccessFunction({
      userId,
      durationHours,
      reason
    });
    return result.data;
  } catch (error) {
    console.error('Error granting creator access:', error);
    throw new Error(error.message || 'Failed to grant creator access');
  }
};

/**
 * Get user's current entitlements and access (Admin only)
 * @param {string} userId - Target user ID
 * @returns {Promise<Object>} User's current access information
 */
export const getUserAccess = async (userId) => {
  try {
    const getUserAccessFunction = httpsCallable(functions, 'getUserAccess');
    const result = await getUserAccessFunction({ userId });
    return result.data;
  } catch (error) {
    console.error('Error getting user access:', error);
    throw new Error(error.message || 'Failed to get user access');
  }
};
